/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Loading from '../../components/Loading';
import { getPermission } from '../../services/users/permissionsApi';
import { notifyApiError } from '../../utils/feedback';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageTitle,
   PageSubtitle,
   PageHeaderText,
   PageHeaderActions,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionHeader,
   SectionTitle,
   SectionBody,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';

export default function PermissionShow() {
   const { id } = useParams();
   const navigate = useNavigate();

   const [permission, setPermission] = useState(null);
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      // FC1 desde 2026-09-15 (0111): o service lê `data.permission` e
      // normaliza `roles[].users` (o alias cru `Users` morreu no Gen3).
      async function loadPermission() {
         setIsLoading(true);

         try {
            const data = await getPermission(id);
            setPermission(data);
         } catch (err) {
            notifyApiError(err);
            if (err?.status === 404) navigate('/permissions');
         } finally {
            setIsLoading(false);
         }
      }

      loadPermission();
   }, [id, navigate]);

   const handleGoBack = () => {
      navigate('/permissions');
   };

   const handleGoToRole = (roleId) => {
      navigate(`/roles/${roleId}`);
   };

   const handleGoToUser = (userId) => {
      navigate(`/users/${userId}`);
   };

   if (!permission && !isLoading) {
      return null;
   }

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Detalhes da Permissão</PageTitle>
               <PageSubtitle>
                  Visualize as informações da permissão e quais roles/usuários
                  possuem acesso.
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <button type="button" onClick={handleGoBack}>
                  Voltar
               </button>
            </PageHeaderActions>
         </PageHeader>

         {/* Informações da Permissão */}
         <Section>
            <SectionHeader>
               <SectionTitle>Informações</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <FormGrid $cols={12} $colsMobile={12}>
                  <Field $col={2} $colMobile={6}>
                     <label>ID</label>
                     <input
                        type="text"
                        value={permission?.id || ''}
                        disabled
                        readOnly
                     />
                  </Field>

                  <Field $col={3} $colMobile={12}>
                     <label>Módulo</label>
                     <input
                        type="text"
                        value={permission?.module || ''}
                        disabled
                        readOnly
                     />
                  </Field>

                  <Field $col={3} $colMobile={12}>
                     <label>Recurso</label>
                     <input
                        type="text"
                        value={permission?.resource || ''}
                        disabled
                        readOnly
                     />
                  </Field>

                  <Field $col={2} $colMobile={6}>
                     <label>Ação</label>
                     <input
                        type="text"
                        value={permission?.action || ''}
                        disabled
                        readOnly
                     />
                  </Field>

                  <Field $col={2} $colMobile={6}>
                     <label>Submódulo</label>
                     <input
                        type="text"
                        value={permission?.submodule || '-'}
                        disabled
                        readOnly
                     />
                  </Field>

                  <Field $col={12} $colMobile={12}>
                     <label>Chave</label>
                     <input
                        type="text"
                        value={permission?.key || ''}
                        disabled
                        readOnly
                     />
                  </Field>

                  <Field $col={12} $colMobile={12}>
                     <label>Label</label>
                     <input
                        type="text"
                        value={permission?.label || '-'}
                        disabled
                        readOnly
                     />
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* Roles que usam esta permissão */}
         <Section>
            <SectionHeader>
               <SectionTitle>
                  Roles com acesso ({permission?.roles?.length || 0})
               </SectionTitle>
            </SectionHeader>

            <SectionBody>
               {!permission?.roles || permission.roles.length === 0 ? (
                  <EmptyState>
                     <h4>Nenhuma role configurada</h4>
                     <p>Esta permissão não está atribuída a nenhuma role.</p>
                  </EmptyState>
               ) : (
                  <TableWrapper>
                     <Table>
                        <thead>
                           <tr>
                              <th>ID</th>
                              <th>Nome</th>
                              <th>Descrição</th>
                              <th style={{ textAlign: 'center' }}>
                                 Usuários
                              </th>
                              <th style={{ textAlign: 'center' }}>Ações</th>
                           </tr>
                        </thead>
                        <tbody>
                           {permission.roles.map((role) => (
                              <tr key={String(role.id)}>
                                 <td>{role.id}</td>
                                 <td>{role.name}</td>
                                 <td>{role.description || '-'}</td>
                                 <td style={{ textAlign: 'center' }}>
                                    {role.users?.length || 0}
                                 </td>
                                 <td style={{ textAlign: 'center' }}>
                                    <button
                                       type="button"
                                       onClick={() => handleGoToRole(role.id)}
                                    >
                                       Ver detalhes
                                    </button>
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </Table>
                  </TableWrapper>
               )}
            </SectionBody>
         </Section>

         {/* Usuários com acesso (através das roles) */}
         <Section>
            <SectionHeader>
               <SectionTitle>Usuários com acesso (auditoria)</SectionTitle>
            </SectionHeader>

            <SectionBody>
               {(() => {
                  // Coleta todos os usuários de todas as roles
                  const allUsers = [];
                  permission?.roles?.forEach((role) => {
                     role.users?.forEach((user) => {
                        // Evita duplicatas
                        if (!allUsers.find((u) => u.id === user.id)) {
                           allUsers.push({ ...user, role_name: role.name });
                        }
                     });
                  });

                  if (allUsers.length === 0) {
                     return (
                        <EmptyState>
                           <h4>Nenhum usuário com acesso</h4>
                           <p>
                              Não há usuários com roles que possuem esta
                              permissão.
                           </p>
                        </EmptyState>
                     );
                  }

                  return (
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th>ID</th>
                                 <th>Nome</th>
                                 <th>Email</th>
                                 <th>Role</th>
                                 <th style={{ textAlign: 'center' }}>Ações</th>
                              </tr>
                           </thead>
                           <tbody>
                              {allUsers.map((user) => (
                                 <tr key={String(user.id)}>
                                    <td>{user.id}</td>
                                    <td>{user.nome}</td>
                                    <td>{user.email}</td>
                                    <td>{user.role_name}</td>
                                    <td style={{ textAlign: 'center' }}>
                                       <button
                                          type="button"
                                          onClick={() =>
                                             handleGoToUser(user.id)
                                          }
                                       >
                                          Ver usuário
                                       </button>
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>
                  );
               })()}
            </SectionBody>
         </Section>
      </Container>
   );
}
